import React from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { insertPoint } from "prosemirror-transform";
import { v4 } from "uuid";

import { showFeatureModal } from "~/billing";
import { billingUpgradeIconDef } from "~/billing/utils";
import { EditorView } from "~/lib/prose/editor";
import { getContext } from "~/lib/prose/plugins/context";
import { isZoom } from "~/lib/utils";
import { __globalModalContext } from "~/ui/modals";
import { ToastActionButton, toasty } from "~/ui/toast";

import { ASSIGNABLE_TYPE_NAMES } from "../assignable";
import { createPosBookmark, PosBookmark } from "../posbookmark";
import EmbedModal from "./EmbedModal";
import SupportedEmbeds from "./supportedServices";

import styles from "~/lib/prose/nodes/embed/embed.module.css";

// ------------------------------------------------------------------------------------------------
// Types
// ------------------------------------------------------------------------------------------------

export type EmbedOption = keyof typeof SupportedEmbeds;
export const embedOptions = Object.keys(SupportedEmbeds) as Array<EmbedOption>;
export interface ExistingEmbed {
	service: EmbedOption;
	nodeToReplace: {
		blockId: string;
		getPos: () => number;
	};
}

export type EmbedNodeAttributes = {
	/* The id of the service (i.e. 'youtube'). */
	service: EmbedOption;

	/* (Optional) The title of the content of the embed (i.e. YouTube video title). */
	title?: string;

	/* The URL used in the iframe. */
	embedUrl: string;

	/* The URL used to derive all the embed information. */
	originalUrl: string;

	/* The ID of the embedded item */
	embedId?: string;
};

export interface EmbedObject {
	/** The name of the service to be displayed to the user (i.e. 'YouTube'). */
	name: string;

	/** The icon displayed in the slash menu. */
	icon: React.ReactNode;

	/** A list of domains which this service can interpret. */
	domains: Array<string>;

	/** A function responsible for validating the given URL and deriving node attributes. */
	getEmbedNodeAttributes: (url: URL) => Promise<EmbedNodeAttributes | Error>;

	/** A function for checking a URL against the list of supported domains for this service. */
	urlIsSupported: (url: URL) => boolean;

	/**
	 * Automatically create an embed for this service if a matching link is
	 * pasted into the note.
	 */
	autoEmbed: boolean;
}

// ------------------------------------------------------------------------------------------------
// Utils
// ------------------------------------------------------------------------------------------------

/**
 * Attempts to convert a string to a URL.
 * @param potentialUrl A string to convert to a URL
 * @returns The URL object.
 */
export function getUrl(potentialUrl: string) {
	try {
		return new URL(potentialUrl);
	} catch {
		return;
	}
}

/**
 * Given a URL, detects if we support it as an embed, and which service it is.
 *
 * @param potentialUrl - A string to be tested
 * @returns The detected service. Null otherwise.
 */
export function detectService(potentialUrl: string): EmbedOption | null {
	const url = getUrl(potentialUrl);
	if (!url) return null;

	for (const [option, { urlIsSupported }] of Object.entries(SupportedEmbeds)) {
		if (urlIsSupported(url)) {
			return option as EmbedOption;
		}
	}

	return null;
}

/**
 * Creates a new Embed node.
 * @param view - Prose's [EditorView](https://prosemirror.net/docs/ref/#view.EditorView)
 * @param embedNodeAttrs - {@link EmbedNodeAttributes}
 * @param replaceRange - The prefered range to be replaced by embed. If not given, current selection location will be used.
 * This arg will not be respected if it splits an assignable item, in which case the embed will be insrted below the item.
 */
export function createEmbedNode(
	{ state, dispatch }: EditorView,
	embedNodeAttrs: EmbedNodeAttributes,
	replaceRange?: [number, number],
) {
	const { tr } = state;
	const embedNodeType = state.schema.nodes.embed;

	let insertPos = state.selection.from;
	if (replaceRange) {
		insertPos = replaceRange[0];
	}
	insertPos = insertPoint(tr.doc, insertPos, embedNodeType) || insertPos;

	const newNode = embedNodeType.createAndFill({
		blockId: v4(),
		...embedNodeAttrs,
	});

	if (!newNode) {
		toasty.error("Sorry, something went wrong while embedding media.");
		return;
	}

	if (replaceRange) {
		const $from = tr.doc.resolve(replaceRange[0]);
		const $to = tr.doc.resolve(replaceRange[1]);
		// We do special things with assignable types.
		// Instead of replacing the range, we remove the range and insert the embed below to prevent spliting of items
		if ($from.depth === $to.depth && $from.sameParent($to) && ASSIGNABLE_TYPE_NAMES.includes($from.parent.type.name)) {
			const endOfParent = $from.end($from.depth);
			tr.insert(endOfParent, newNode);
			tr.deleteRange($from.pos, $to.pos);
		} else {
			tr.replaceRangeWith(replaceRange[0], replaceRange[1], newNode);
		}
	} else {
		tr.insert(insertPos, newNode);
	}
	dispatch(tr);
}

/**
 * Generates the necessary data for an embed and calls the creation function
 * @param input - The desired URL to embed
 * @param view - The current EditorView
 * @param replaceRange - The prefered range to be replaced by embed. If not given, current selection location will be used.
 * This arg will not be respected if it splits an assignable item, in which case the embed will be insrted below the item.
 */
async function insertEmbed(view: EditorView, input: string, embed: EmbedObject, replaceRange?: [number, number]) {
	const url = getUrl(input);
	if (!url) return false;
	let posBookmarks: [PosBookmark, PosBookmark] | null = null;
	if (replaceRange !== undefined) {
		posBookmarks = [createPosBookmark(view.state, replaceRange[0]), createPosBookmark(view.state, replaceRange[1])];
	}
	const embedAttributes = await embed.getEmbedNodeAttributes(url);
	if (!embedAttributes || embedAttributes instanceof Error) return false;

	//IMPORTANT: view must be called LAST here to ensure view.state is not outdated
	createEmbedNode(view, embedAttributes, posBookmarks?.map(bm => bm.mapToNow()) as [number, number] | undefined);
	posBookmarks?.forEach(bm => bm.discard());
	return true;
}

/**
 * Checks to see if the given URL has already been used to create an embed in the current document
 * @param view - The current EditorView
 * @param url - The URL to be checked
 * @returns - True if there is an embed node for the given URL in the document
 */
function embedExistsInNote(view: EditorView, url: string): boolean {
	const { content } = view.state.doc;

	for (let i = 0; i < content.childCount; i++) {
		let currentNode = content.child(i);
		if (currentNode.type.name === "embed" && currentNode.attrs["originalUrl"] === url) return true;
	}

	return false;
}

/**
 * Takes any url and attempts to create an embed
 * @param view - Prose's [EditorView](https://prosemirror.net/docs/ref/#view.EditorView)
 * @param input - The url to attempt to embed
 * @param automatic - True if we're attempting to embed without the user explicitly deciding to do so.
 */
export function attemptToEmbed(
	view: EditorView,
	input: string,
	automatic = true,
	replaceRange: [number, number],
): Promise<[noFurtherHandlingRequired: boolean, nowFrom: number, nowTo: number]> {
	const posBookmarks: readonly [PosBookmark, PosBookmark] = replaceRange.map(pos =>
		createPosBookmark(view.state, pos),
	) as [PosBookmark, PosBookmark];
	function bookMarksToArray() {
		return posBookmarks.map(bm => bm.mapToNow()) as [number, number];
	}
	function discardBookMarks() {
		posBookmarks.forEach(bm => bm.discard());
	}
	return new Promise<[boolean, number, number]>(resolve => {
		// Check if this link could be embedded
		const embedName = detectService(input);
		if (!embedName) return resolve([false, ...bookMarksToArray()]);

		const embed = getEmbedObject(embedName);
		if (automatic && !embed.autoEmbed) return resolve([false, ...bookMarksToArray()]);

		const context = getContext(view.state);
		const hasAccessToEmbeds = context.billing?.hasAccessToEmbeds;

		if (hasAccessToEmbeds) {
			// Show the duplication toast only if they have access to embeds
			if (embedExistsInNote(view, input)) {
				const pillNodeType = view.state.schema.nodes["link_pill"];
				let currentRange: [number, number] = [...replaceRange];
				if (pillNodeType) {
					// insert it as pills eagerly, and replace it with embeds when the user confirms.
					const newPill = pillNodeType.createAndFill({ entityId: v4(), url: input }, view.state.schema.text(input));
					if (newPill) {
						view.dispatch(view.state.tr.replaceRangeWith(replaceRange[0], replaceRange[1], newPill));
						const newRangeEnd = posBookmarks[0].mapToNow();
						currentRange[0] = newRangeEnd - newPill.nodeSize;
						currentRange[1] = newRangeEnd;
					}
				}

				function onClick() {
					insertEmbed(view, input, embed, currentRange);
				}

				toasty.info("This link has already been embedded in this note. Want to embed it again?", {
					autoClose: false,
					closeButton: true,
					className: cx(styles.toast, styles.WithAction),
					// this is to make sure resolve is called at some point so the `finally` branch is triggered but not too soon.
					onClose: () => resolve([true, ...bookMarksToArray()]),
					// eslint-disable-next-line fellow/no-inline-disable
					// eslint-disable-next-line react/jsx-no-bind -- Never gets re-rendered
					actions: <ToastActionButton onClick={onClick}>Embed media</ToastActionButton>,
				});
			} else {
				insertEmbed(view, input, embed, replaceRange).then(res => resolve([res, ...bookMarksToArray()]));
			}
		} else {
			// Prevent embeds from being placed if the user is on a free plan
			const ToastButton = () => {
				const showModal = React.useCallback(() => showFeatureModal("embeds"), []);
				return (
					<ToastActionButton onClick={showModal} variant="billing">
						<FontAwesomeIcon className={styles.crownIcon} icon={billingUpgradeIconDef} />
						&ensp;Learn more
					</ToastActionButton>
				);
			};
			toasty.info(`Your ${embed.name} link could be embedded into this note.`, {
				actions: <ToastButton />,
			});
			return resolve([false, ...bookMarksToArray()]);
		}
	}).finally(() => {
		discardBookMarks();
	});
}

/**
 * Edit's the attributes of an embed node
 * @param view - Prose's [EditorView](https://prosemirror.net/docs/ref/#view.EditorView)
 * @param embedNodeAttrs - {@link EmbedNodeAttributes}
 * @param nodeToReplace.blockId - The ID of the embed to replace.
 * @param nodeToReplace.getPos - A function that returns the position of the embed to be replaced
 */
export function editEmbedNode(
	{ state, dispatch }: EditorView,
	embedNodeAttrs: EmbedNodeAttributes,
	{ blockId, getPos }: ExistingEmbed["nodeToReplace"],
) {
	const somethingWentWrong = (error: Error) => {
		toasty.error("Sorry, something went wrong editing this media embed.");
		console.error(error);
		return;
	};

	const { tr } = state;

	try {
		// Will throw if editing after the embed is deleted by another user
		const embedPos = getPos();

		const node = state.doc.resolve(embedPos).nodeAfter;
		if (node?.attrs.blockId !== blockId) throw new Error(`Block ID doesn't match`);

		tr.setNodeMarkup(embedPos, state.schema.nodes.embed, { blockId, ...embedNodeAttrs });
		dispatch(tr);
	} catch (error) {
		if (error instanceof Error) {
			return somethingWentWrong(error);
		}
	}
}

/**
 * Checks if a string is a valid service for embeds.
 */
export function isEmbedOption(service: string): service is EmbedOption {
	return Object.keys(SupportedEmbeds).includes(service);
}

/** Retrieve the embed details of a supported service.
 * @param service - A supported service
 * @returns The embed details of a supported service.
 */
export function getEmbedObject(service: EmbedOption): EmbedObject {
	return SupportedEmbeds[service];
}

export function getSpotifyTypeAndId(url: URL): undefined | { type: string; id: string } {
	const regexResult = url
		.toString()
		.match(/https:\/\/open.spotify.com(?:\/embed)?\/(playlist|album|track|artist|show|episode)\/(\w+)(?:\?si=\w+)?/);

	if (regexResult === null) return;

	const contentType = regexResult[1]; //playlist, track, or album
	const contentId = regexResult[2];

	return {
		type: contentType,
		id: contentId,
	};
}

/** Open the embed modal.
 * @throws If in API calls are prohibited in this environment (i.e. in Zoom).
 */
export function launchEmbedModal(view: EditorView, existingEmbed?: ExistingEmbed): void {
	if (isZoom) {
		toasty.warn("Sorry, embeds are unavailable in Zoom.");
		return;
	}

	const context = getContext(view.state);

	/* It's going to take a bunch of work to get the billing context in some editors,
		so for now we'll allow free users to embed stuff when the context isn't available \_(ツ)_/¯ */
	if (!(existingEmbed && existingEmbed.service === "loom") && context.billing && !context.billing.hasAccessToEmbeds) {
		return showFeatureModal("embeds");
	}

	__globalModalContext?.open<string | null>(({ close }) => (
		<EmbedModal existingEmbed={existingEmbed} close={close} view={view} />
	));
}
